import React, { useCallback, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Col, notification, Row, Typography } from "antd";
import moment from "moment/moment";
import { useHistory, useLocation } from "react-router";

import ActivityService from "@Services/ActivityService";
import ActivityView from "@Components/activity/ActivityView";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import IconButton from "@Components/shared/IconButton";
import ProfileCompact from "@Components/user/ProfileCompact";
import SessionManager from "@Core/session";
import VideoTag from "@Components/activity/VideoTag";
import useActivityDetailsPresenter from "@Components/activity/hooks/useActivityDetailsPresenter";
import { QueryKeys } from "@Constants";
import { useCurrentActivityQuery } from "@Components/activity/queries";
import { useSearchParams, getActivityLabel } from "@Utils";
import useAssociatedStatuses from "./hooks/useAssociatedStatuses";
import { IActivityDetails } from "@Models/IActivityDetails";
import PageHelmet from "@Components/shared/PageHelmet";

const { Title, Text } = Typography;

interface IProps {
    clearCurrentActivity: () => void;
    activities: IActivityDetails[];
}

const ActivityDetailsPage: React.FC<IProps> = ({ clearCurrentActivity, activities }) => {
    const location = useLocation();
    const { userId: loggedInUserId } = SessionManager.getServiceUser();
    const [searchParams] = useSearchParams();
    const [_deleting, setDeleting] = useState<boolean>(false);
    const urlParamUserId = new URLSearchParams(location.search).get("user");
    const urlParamActivityId = searchParams.get("activity");
    const [currentActivityId, setCurrentActivityId] = useState<string>("");
    const [currentUserId, setCurrentUserId] = useState<string>(loggedInUserId);
    const history = useHistory();
    const queryClient = useQueryClient();

    // TODO: move all ui logic to presenter
    const { connection, connections } = useActivityDetailsPresenter(currentActivityId);

    const resetActivity = useCallback(() => {
        setCurrentActivityId("");
        history.push(`/activity?user=${SessionManager.getServiceUser()?.userId}`);
        setCurrentUserId(SessionManager.getServiceUser()?.userId);
    }, [history]);

    const getCurrentUrlTerms = useCallback(async () => {
        if (urlParamUserId !== null) {
            if (urlParamUserId !== currentUserId) {
                setCurrentUserId(urlParamUserId);
            }
        }

        if (urlParamActivityId !== null) {
            setCurrentActivityId(urlParamActivityId);
        }
    }, [currentUserId, urlParamActivityId, urlParamUserId]);

    const { data: currentActivity, refetch } = useCurrentActivityQuery(
        currentActivityId,
        QueryKeys.ActivityCurrent,
        resetActivity
    );

    useEffect(() => {
        refetch();
    }, [currentActivityId]);

    const handleDelete = async (activityInstanceId: string) => {
        const service = new ActivityService();
        setDeleting(true);
        const result = await service.delete(activityInstanceId);
        setDeleting(false);
        if (!result.hasErrors) {
            await queryClient.invalidateQueries([QueryKeys.ActivitySearchResults]);
            await queryClient.invalidateQueries([QueryKeys.ActivityListUser]);
            await queryClient.invalidateQueries([QueryKeys.ActivityListPublic]);
            await queryClient.invalidateQueries([QueryKeys.ActivityProgression]);
            await queryClient.invalidateQueries([QueryKeys.ActivityCurrent]);
            notification.success({ message: "Activity deleted." });
        } else {
            notification.error({ message: "There was an error deleting the activity." });
        }
    };

    /**
     * Open an activity on select.
     * @param userId
     * @param activityInstanceId
     */
    const handleActivitySelect = useCallback((userId: string, activityInstanceId: string) => {
        history.push(`/activity?user=${userId}&activity=${activityInstanceId}`);
        setCurrentActivityId(activityInstanceId);
    }, []);

    useEffect(() => {
        const checkUrlTerms = async () => {
            await getCurrentUrlTerms();
        };
        checkUrlTerms().catch(() => {});
    }, [getCurrentUrlTerms, urlParamUserId]);

    const render = currentActivity !== undefined && currentActivity !== null;

    const { associatedStatusesMap, isHydrated } = useAssociatedStatuses({
        activities: currentActivity ? [currentActivity] : [],
        loggedInUser: currentUserId,
    });

    const associatedStatus = associatedStatusesMap.get(urlParamActivityId);

    const onBackSelect = () => {
        history.goBack();
        clearCurrentActivity();
    };

    const activityTitle = currentActivity?.activityMeta?.title;

    return (
        <>
            <PageHelmet name={`Activity${!!activityTitle ? ` - ${activityTitle}` : ""}`} />
            {render ? (
                <>
                    <IconButton
                        icon={<ArrowBackIosNewIcon style={{ height: "14px" }} />}
                        className="secondary flex"
                        onClick={onBackSelect}
                    >
                        Back
                    </IconButton>
                    <Row justify="space-between">
                        <Col>
                            <div style={{ marginBottom: "10px" }}>
                                <Title level={4}>{getActivityLabel(currentActivity)}</Title>
                                <Text>{moment(currentActivity?.creationDate).format("ddd, MMM Do YYYY, h:mm a")}</Text>
                                <VideoTag mediaStatus={currentActivity?.mediaStatus} />
                            </div>
                        </Col>
                        <Col>
                            {!!connection ? (
                                <ProfileCompact
                                    render={true}
                                    fullName={connection?.fullName ?? ""}
                                    countryCode={connection?.countryCode ?? ""}
                                    organisation={connection?.organisation ?? ""}
                                    connectionDate={connection?.connectionDate}
                                />
                            ) : null}
                        </Col>
                    </Row>
                </>
            ) : null}
            <ActivityView
                activity={currentActivity}
                handleDelete={handleDelete}
                handleActivitySelect={handleActivitySelect}
                render={render}
                associatedStatus={associatedStatus}
                isHydrated={isHydrated}
                userConnections={connections ?? []}
            />
        </>
    );
};

export default ActivityDetailsPage;
